// CORP-8479: cache dos relatórios lentos do corp-backend (monitor/gestation/maternity e o histórico
// por período). Guardado em sessionStorage (sobrevive ao F5, some ao fechar a aba) e isolado por
// usuário + holding + super holding + requisição à API + tela/filtros da URL: nada que mude o
// resultado pode ficar fora da chave, senão a tela mostra dado de outra consulta.
//
// O miolo (armazenamento, validade, deduplicação, escopo) é o `@front-persist-state/core`; aqui ficam
// só as regras da CORP: de que é feita a chave e quanto tempo cada tipo de relatório vale.
import {
  cached,
  clearAll,
  keepOnlyPrefix,
  normalizedLocation as normalizeLocation,
  scopedKey,
  stableStringify,
} from '@front-persist-state/core';

export const REPORT_TTL_OPEN_MS = 5 * 60 * 1000;
export const REPORT_TTL_CLOSED_MS = 15 * 60 * 1000;

export interface ReportKeyParts {
  userId?: string;
  holdingId?: string;
  superHolding: boolean;
  // Identidade da requisição à API (cliente + caminho + parâmetros ordenados): o que o servidor
  // recebe. Mesma requisição, mesma chave.
  request: string;
  // Tela e filtros da URL do navegador, normalizados (ver `normalizedLocation`).
  location: string;
}

export function normalizedLocation(
  pathname = window.location.pathname,
  search = window.location.search,
): string {
  return normalizeLocation({ pathname, search });
}

export function stableParams(value: unknown): string {
  return stableStringify(value);
}

// Sem usuário ou sem holding não há como isolar o dado: devolve null e a chamada não usa cache.
export function buildReportKey(parts: ReportKeyParts): string | null {
  return scopedKey(
    { user: parts.userId, holding: parts.holdingId, super: parts.superHolding },
    parts.request,
    parts.location,
  );
}

// Relatório "em aberto" (tempo real) vence em 5 min; fechado, em 15. Sem a informação, o mais curto.
export function reportTtl(isOpen: string | boolean | undefined): number {
  return String(isOpen) === 'false' ? REPORT_TTL_CLOSED_MS : REPORT_TTL_OPEN_MS;
}

export function cachedReport<T>(
  key: string | null,
  ttlMs: number,
  load: () => Promise<T>,
  options: { bypass?: boolean } = {},
): Promise<T> {
  return cached({ key, ttlMs, load, storage: 'session', bypass: options.bypass });
}

export function clearReportCache(): void {
  clearAll('session');
}

// Troca de usuário no mesmo navegador: descarta tudo que não é do usuário atual.
export function purgeReportCacheExceptUser(userId: string): void {
  keepOnlyPrefix(`user=${userId}|`, 'session');
}
